import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgId } from "@/hooks/useOrganization";
import { useCurrentPerson } from "@/hooks/useCurrentPerson";
import { toast } from "sonner";
import { getErrorDescription } from "@/lib/user-error";
import { createTaskAssignmentNotification } from "@/lib/task-notifications";
import { recordTasksCreateHttpStatus } from "@/monitoring/tasks-create-monitoring";
import { isStatusOnlyTaskUpdate, mergeTaskIntoCachedList } from "@/lib/task-status-overlay";
import type { Database } from "@/integrations/supabase/types";

type TaskRow = Database["public"]["Tables"]["tasks"]["Row"];
type TaskUpdate = Database["public"]["Tables"]["tasks"]["Update"];

export type TaskStatus = Database["public"]["Enums"]["task_status"];
export type TaskPriority = Database["public"]["Enums"]["task_priority"];

export interface TaskWithRelations extends TaskRow {
  project?: { id: string; name: string } | null;
}

const TASK_RELATION_BATCH_SIZE = 100;

function compareTasksByDisplayOrder(a: TaskWithRelations, b: TaskWithRelations) {
  if (a.due_date !== b.due_date) {
    if (!a.due_date) return 1;
    if (!b.due_date) return -1;
    return a.due_date.localeCompare(b.due_date);
  }

  if (a.created_at !== b.created_at) {
    return b.created_at.localeCompare(a.created_at);
  }

  return a.id.localeCompare(b.id);
}

interface TaskFilters {
  status?: TaskStatus | "all";
  projectId?: string;
  assigneeId?: string;
  requestId?: string;
  search?: string;
  /** Fetch every matching task in pages instead of the standard 2,000-row cap. */
  fetchAll?: boolean;
  /** Bound a non-catalog task query for a searchable picker. */
  limit?: number;
}

export function useTasks(filters: TaskFilters = {}, options: { enabled?: boolean; cacheScope?: string } = {}) {
  const organizationId = useOrgId();
  // PostgreSQL normalizes UUID output to lowercase. Normalize input filters as
  // well so the snapshot's defensive client-side comparison cannot hide rows
  // from an otherwise valid uppercase deep link.
  const normalizedFilters = {
    ...filters,
    projectId: filters.projectId?.toLowerCase(),
    requestId: filters.requestId?.toLowerCase(),
    assigneeId: filters.assigneeId?.toLowerCase(),
  };

  return useQuery({
    queryKey: options.cacheScope
      ? ["tasks", organizationId, normalizedFilters, options.cacheScope]
      : ["tasks", organizationId, normalizedFilters],
    queryFn: async () => {
      if (!organizationId) return [];

      const taskMatchesFilters = (task: TaskWithRelations) => {
        if (task.deleted_at) return false;
        if (
          normalizedFilters.status &&
          normalizedFilters.status !== "all" &&
          task.status !== normalizedFilters.status
        ) {
          return false;
        }
        if (normalizedFilters.projectId && task.project_id !== normalizedFilters.projectId) return false;
        if (normalizedFilters.requestId && task.request_id !== normalizedFilters.requestId) return false;
        if (normalizedFilters.assigneeId && task.assignee_id !== normalizedFilters.assigneeId) return false;
        return (
          !normalizedFilters.search ||
          task.title.toLowerCase().includes(normalizedFilters.search.toLowerCase())
        );
      };

      const createQuery = () => {
        let query = supabase
          .from("tasks")
          .select("*, project:projects(id, name)")
          .eq("organization_id", organizationId);

        query = query.is("deleted_at", null);

        if (normalizedFilters.status && normalizedFilters.status !== "all") {
          query = query.eq("status", normalizedFilters.status);
        }

        if (normalizedFilters.projectId) {
          query = query.eq("project_id", normalizedFilters.projectId);
        }

        if (normalizedFilters.requestId) {
          query = query.eq("request_id", normalizedFilters.requestId);
        }

        if (normalizedFilters.assigneeId) {
          query = query.eq("assignee_id", normalizedFilters.assigneeId);
        }

        if (normalizedFilters.search) {
          query = query.ilike("title", `%${normalizedFilters.search}%`);
        }

        return query
          .order("due_date", { ascending: true, nullsFirst: false })
          .order("created_at", { ascending: false })
          .order("id", { ascending: true });
      };

      if (normalizedFilters.fetchAll) {
        // A JSON aggregate is one database statement, so PostgreSQL gives the
        // entire catalog a single MVCC snapshot. That avoids the gaps that a
        // client-side keyset scan cannot close: a random UUID inserted behind a
        // cursor, or a soft-deleted row that the restrictive SELECT policy hides
        // before reconciliation can observe its tombstone.
        const { data, error } = await supabase.rpc("get_tasks_catalog_snapshot", {
          p_organization_id: organizationId,
          p_status:
            normalizedFilters.status && normalizedFilters.status !== "all"
              ? normalizedFilters.status
              : null,
          p_project_id: normalizedFilters.projectId ?? null,
          p_request_id: normalizedFilters.requestId ?? null,
          p_assignee_id: normalizedFilters.assigneeId ?? null,
          p_search: normalizedFilters.search ?? null,
        });
        if (error) throw error;
        const tasks = (Array.isArray(data) ? data : []) as unknown as TaskWithRelations[];
        return tasks.filter(taskMatchesFilters).sort(compareTasksByDisplayOrder);
      }

      const limit = Math.min(Math.max(normalizedFilters.limit ?? 2000, 1), 2000);
      const { data, error } = await createQuery().limit(limit); // Safety net: prevents silent truncation at Supabase max_rows=1000
      if (error) throw error;
      return (data || []) as TaskWithRelations[];
    },
    enabled: !!organizationId && (options.enabled ?? true),
  });
}

export interface TaskUpdateInput {
  id: string;
  updates: TaskUpdate;
  /** Atomically refuse a stale edit or Undo, preserving intervening writes. */
  expectedUpdates?: TaskUpdate;
}

export function useUpdateTask(options?: { notifySuccess?: boolean; notifyError?: boolean; cacheScope?: string }) {
  const queryClient = useQueryClient();
  const organizationId = useOrgId();
  const { data: currentPerson } = useCurrentPerson({ cacheScope: options?.cacheScope });

  return useMutation({
    mutationFn: async ({ id, updates, expectedUpdates }: TaskUpdateInput) => {
      if (!organizationId) throw new Error("No organization selected");

      let query = supabase
        .from("tasks")
        .update(updates)
        .eq("id", id)
        .eq("organization_id", organizationId);
      if (expectedUpdates) {
        query = query.is("deleted_at", null);
        for (const [field, expected] of Object.entries(expectedUpdates)) {
          if (expected === undefined) throw new Error("Reload the task before editing.");
          query = expected === null ? query.is(field, null) : query.eq(field as keyof TaskRow, expected);
        }
      }
      const { data, error } = await query.select().single();
      if (error?.code === "PGRST116" && expectedUpdates) {
        throw new Error("This task changed or is no longer available. Reload it before retrying.");
      }
      if (error) throw error;

      // Create notification if the update includes an assignee
      if (updates.assignee_id && organizationId && currentPerson) {
        const actorName = `${currentPerson.first_name ?? ""} ${currentPerson.last_name ?? ""}`.trim() || "Someone";

        createTaskAssignmentNotification({
          organizationId,
          taskId: id,
          taskTitle: data.title,
          taskDescription: data.description,
          assigneeUserId: updates.assignee_id,
          actorPersonId: currentPerson.id,
          actorName,
          previousAssigneeUserId: null,
        }).catch((error) => {
          console.error("Failed to create task assignment notification:", error);
          // Don't throw - notification failure shouldn't block task update
        });
      }

      return data as TaskRow;
    },
    onSuccess: (data, variables) => {
      // Only status transitions can safely reuse this mutation's bare task row.
      // Cached task lists also carry the joined `project` relation; spreading a
      // project reassignment response into one would retain the previous joined
      // object beside its new project_id. Project updates refetch instead.
      if (isStatusOnlyTaskUpdate(variables.updates)) {
        // A status change can leave one filtered query refreshed before the
        // broad query; synchronize cached copies immediately for dependencies.
        queryClient.setQueriesData<TaskWithRelations[]>(
          { queryKey: ["tasks", organizationId] },
          (cached) => mergeTaskIntoCachedList(cached, data as TaskWithRelations),
        );
      }
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["task-record"] });
      if ("project_id" in variables.updates) {
        // The task's project changed (linked, reassigned, or cleared). Invalidate
        // every project-detail query — narrowly invalidating only the new
        // project_id would leave the PREVIOUS project's detail page still showing
        // this task until an unrelated refetch (stale on reassign/unlink).
        queryClient.invalidateQueries({ queryKey: ["project"] });
        queryClient.invalidateQueries({ queryKey: ["projects"] });
      } else if (data.project_id) {
        queryClient.invalidateQueries({ queryKey: ["project", data.project_id] });
        queryClient.invalidateQueries({ queryKey: ["projects"] });
      }
      if (data.request_id) {
        queryClient.invalidateQueries({ queryKey: ["request", data.request_id] });
      }
      if (options?.notifySuccess !== false) toast.success("Task updated");
    },
    onError: (error) => {
      if (options?.notifyError === false) return;
      toast.error("Failed to update task", {
        description: getErrorDescription(error),
      });
    },
  });
}

// #4999: status-only transitions for the CURRENT assignee go through
// update_own_task_status (a SECURITY DEFINER RPC, see
// supabase/migrations/20260810120000_task_assignee_status_only_rpc.sql)
// rather than a direct table UPDATE. tasks_update_authenticated/
// tasks_update_anon no longer grant a raw `assignee_id = auth.uid()` branch,
// so a non-staff assignee (VolunteerTasks.tsx, and AppLayout's global
// notification quick action, which VolunteerShell also exposes to
// volunteers) can no longer PATCH arbitrary task columns -- only
// todo/in_progress/done, and only on their own assigned task. Staff-only
// surfaces (Tasks.tsx, ProjectDetail.tsx, Calendar drag-reschedule) keep
// using useUpdateTask() above; they remain authorized by the policies'
// separate staff branch.
export function useUpdateTaskStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: string;
      status: Extract<TaskStatus, "todo" | "in_progress" | "done">;
    }) => {
      const { data, error } = await supabase.rpc(
        "update_own_task_status" as never,
        { p_task_id: id, p_status: status } as never
      );
      if (error) throw error;
      return data as unknown as TaskRow;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["task-record"] });
      if (data.project_id) {
        queryClient.invalidateQueries({ queryKey: ["project", data.project_id] });
        queryClient.invalidateQueries({ queryKey: ["projects"] });
      }
      if (data.request_id) {
        queryClient.invalidateQueries({ queryKey: ["request", data.request_id] });
      }
      toast.success("Task updated");
    },
    onError: (error) => {
      toast.error("Failed to update task", {
        description: getErrorDescription(error),
      });
    },
  });
}

export function useCreateTask() {
  const queryClient = useQueryClient();
  const organizationId = useOrgId();
  const { data: currentPerson } = useCurrentPerson();

  return useMutation({
    mutationFn: async (input: {
      title: string;
      description?: string;
      priority?: TaskPriority;
      dueDate?: string;
      projectId?: string;
      requestId?: string;
      assigneeId?: string;
    }) => {
      if (!organizationId) throw new Error("No organization selected");

      // create_task_locked (not a bare insert): when projectId is set, it
      // takes the same FOR UPDATE lock on the project row that
      // delete_project_cascade does, so a task can't be created against a
      // project that's concurrently (or already) being soft-deleted — see
      // PR #5614.
      const { data, error } = await supabase
        .rpc("create_task_locked", {
          p_organization_id: organizationId,
          p_title: input.title,
          p_description: input.description || undefined,
          p_priority: input.priority || "medium",
          p_due_date: input.dueDate || undefined,
          p_project_id: input.projectId || undefined,
          p_request_id: input.requestId || undefined,
          p_assignee_id: input.assigneeId || undefined,
        })
        .single();

      if (error) {
        const code = error.code;
        const message = error.message;
        if (code === "42P01") {
          recordTasksCreateHttpStatus(404);
        } else if (code === "23503") {
          recordTasksCreateHttpStatus(404);
          throw new Error("This project no longer exists or has been deleted.");
        } else if (code === "PGRST116" || message.includes("0 rows")) {
          // RLS rejected the insert — user lacks required role
          recordTasksCreateHttpStatus(403);
          throw new Error("Permission denied: you may not have the required role to create tasks in this organization.");
        } else if (code === "42501") {
          recordTasksCreateHttpStatus(403);
          throw new Error("Permission denied: insufficient database privileges to create tasks.");
        } else if (code === "23502") {
          recordTasksCreateHttpStatus(400);
        } else {
          recordTasksCreateHttpStatus(500);
        }
        throw error;
      }

      recordTasksCreateHttpStatus(201);

      // Create notification if task is assigned to someone
      if (input.assigneeId && organizationId && currentPerson) {
        const actorName = `${currentPerson.first_name ?? ""} ${currentPerson.last_name ?? ""}`.trim() || "Someone";

        createTaskAssignmentNotification({
          organizationId,
          taskId: data.id,
          taskTitle: data.title,
          taskDescription: data.description,
          assigneeUserId: input.assigneeId,
          actorPersonId: currentPerson.id,
          actorName,
          previousAssigneeUserId: null,
        }).catch((error) => {
          console.error("Failed to create task assignment notification:", error);
          // Don't throw - notification failure shouldn't block task creation
        });
      }

      return data as TaskRow;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["task-record"] });
      // A new task on a project / request appears in their embedded task
      // lists; without these the parent detail views stay stale.
      if (data.project_id) {
        queryClient.invalidateQueries({ queryKey: ["projects"] });
        queryClient.invalidateQueries({ queryKey: ["project", data.project_id] });
      }
      if (data.request_id) {
        queryClient.invalidateQueries({ queryKey: ["request", data.request_id] });
      }
      toast.success("Task created", {
        description: `"${data.title}" has been added to the task list.`,
      });
    },
    onError: (error) => {
      toast.error("Failed to create task", {
        description: getErrorDescription(error),
      });
    },
  });
}

export function useDeleteTask() {
  const queryClient = useQueryClient();
  const organizationId = useOrgId();

  return useMutation({
    mutationFn: async (taskId: string) => {
      if (!organizationId) throw new Error("No organization selected");

      // Soft-delete the task and drop its dependency edges atomically inside
      // the RPC (not a separate client-side delete): task_dependencies'
      // delete policy is admin/staff-only, stricter than tasks' own delete
      // gate, so a write-capable-but-not-staff caller would have a
      // client-side dependency delete silently denied while the task
      // soft-delete still succeeded, leaving dangling edges.
      const { data, error } = await supabase.rpc("set_tasks_deleted_at", {
        p_organization_id: organizationId,
        p_ids: [taskId],
        p_deleted_at: new Date().toISOString(),
      });

      if (error) throw error;
      const updated = data?.[0];
      return { taskId, projectId: updated?.project_id ?? null, requestId: updated?.request_id ?? null };
    },
    onSuccess: ({ projectId, requestId }) => {
      // Tasks lists, dependency edges, and any embedded-task views on
      // parent project / request all need to drop the row.
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["task-record"] });
      queryClient.invalidateQueries({ queryKey: ["task-dependencies"] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      }
      if (requestId) {
        queryClient.invalidateQueries({ queryKey: ["request", requestId] });
      }
      toast.success("Task deleted");
    },
    onError: (error) => {
      toast.error("Failed to delete task", {
        description: getErrorDescription(error),
      });
    },
  });
}

export interface TaskDependency {
  id: string;
  task_id: string;
  depends_on_task_id: string;
}

export function useTaskDependencies(taskIds: string[], options?: { cacheScope?: string }) {
  const organizationId = useOrgId();

  return useQuery({
    queryKey: ["task-dependencies", organizationId, taskIds, ...(options?.cacheScope === undefined ? [] : [{ cacheScope: options.cacheScope }])],
    queryFn: async () => {
      if (!organizationId || taskIds.length === 0) return [];
      const uniqueTaskIds = Array.from(new Set(taskIds)).filter(Boolean);
      const dependencies: TaskDependency[] = [];

      // UUID lists grow past common proxy URL limits surprisingly quickly. Keep
      // each PostgREST `in` clause bounded so a large project cannot make the
      // dependency editor appear empty and overwrite its existing links.
      for (let index = 0; index < uniqueTaskIds.length; index += TASK_RELATION_BATCH_SIZE) {
        const taskIdBatch = uniqueTaskIds.slice(index, index + TASK_RELATION_BATCH_SIZE);

        // Fetch a bounded set of task IDs as one PostgreSQL snapshot. A UUID
        // cursor can survive deletes but cannot see an inserted edge that sorts
        // before it; returning one JSON aggregate removes that destructive-save
        // path altogether.
        const { data, error } = await supabase.rpc("get_task_dependencies_snapshot", {
          p_organization_id: organizationId,
          p_task_ids: taskIdBatch,
        });
        if (error) throw error;
        dependencies.push(...((Array.isArray(data) ? data : []) as unknown as TaskDependency[]));
      }

      return dependencies;
    },
    enabled: !!organizationId && taskIds.length > 0,
  });
}

export function useUpdateTaskDependencies(options?: { notifySuccess?: boolean }) {
  const queryClient = useQueryClient();
  const organizationId = useOrgId();

  return useMutation({
    mutationFn: async ({
      taskId,
      dependsOnIds,
      expectedDependsOnIds,
    }: {
      taskId: string;
      dependsOnIds: string[];
      expectedDependsOnIds: string[];
    }) => {
      if (!organizationId) throw new Error("No organization");
      // The RPC locks the task row, compares the loaded edge set to this draft's
      // opening snapshot, and only then replaces it. A concurrent insertion can
      // therefore cause a safe conflict/reload instead of being silently deleted.
      const { error } = await supabase.rpc("replace_task_dependencies", {
        p_organization_id: organizationId,
        p_task_id: taskId,
        p_expected_depends_on_ids: expectedDependsOnIds,
        p_depends_on_ids: dependsOnIds,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task-dependencies"] });
      if (options?.notifySuccess !== false) toast.success("Dependencies updated");
    },
    onError: async (error) => {
      // A stale dependency draft is expected after another user saves first.
      // Refresh the mounted dependency query before the user reopens the
      // editor, otherwise its opening snapshot would immediately conflict too.
      if (
        typeof error === "object" &&
        error !== null &&
        "code" in error &&
        error.code === "40001"
      ) {
        await queryClient.invalidateQueries({
          queryKey: ["task-dependencies"],
          refetchType: "active",
        });
      }
      toast.error("Failed to update dependencies", {
        description: getErrorDescription(error),
      });
    },
  });
}
